import React, { useCallback, useMemo, useState, useEffect } from "react";
import { ConfigProvider, Modal, Table, Tooltip } from "antd";
import Style from '../../style';
import Input, { BasicInput } from "../input";
import useAjaxModal from "./useAjaxModal";
import i18next from "i18next";
import { useGeneralContext } from "../../../../context/General";
import { EyeOutlined } from "@ant-design/icons";
import { ModalHeader } from "datastake-daf/dist/components";
import { formatClassname } from "datastake-daf/src/helpers/ClassesHelper";
import { useTranslation } from "react-i18next";
import { showHideInput } from "../../../EditTIF/helper";
import { tooltipInputs } from "../../../../helpers/form";
import { getLocales } from "../../../../components/Table/newTable";

export default function DataLink({
  form = {},
  values,
  formData = [],
  k,
}) {
  const { t } = useTranslation();
  const isSingle = useMemo(() => form?.meta?.maxRepeat === 1, [form]);
  const [modalRow, setModalRow] = useState(null)
  const [linkingData, setLinkingData] = useState({});
  const { ajaxOptions } = useGeneralContext();
  const { automaticallyLink } = useMemo(() => form, [form]);
  const entity = useMemo(() => automaticallyLink?.entity || form?.meta?.dataLinkEntity, [automaticallyLink, form]);
  const isAjaxModal = useMemo(() => !!form?.meta?.namespace, [form]);
  const notAvailable = useMemo(() => (values?.meta?.inputs || {})[k]?.notAvailable, [values, k]);
  const notApplicable = useMemo(() => (values?.meta?.inputs || {})[k]?.notApplicable, [values, k]);

  const noBody = notApplicable || notAvailable;

  const formScope = useMemo(() => {
    const val = form.meta.formScope;
    let toReturn = val;

    if (val) {
      if (typeof val === 'object') {
        Object.keys(val).forEach((k) => {
          const show = showHideInput(
            { showIf: k },
            values,
          );

          if (show) {
            toReturn = val[k];
          }
        })
      }
    }

    return toReturn;
  }, [form, values]);

  const tableKeys = useMemo(() => {
    let val = form?.meta?.tableKeys;
    let toReturn = val;

    if (val && !Array.isArray(val)) {
      if (typeof val === 'object') {
        Object.keys(val).forEach((k) => {
          const show = showHideInput(
            { showIf: k },
            values,
          );

          if (show) {
            toReturn = val[k];
          }
        })
      }
    }

    return toReturn;
  }, [form, values]);

  useEffect(() => {
    if (values.linking && values.linking[entity] && values.linking[entity]) {
      setLinkingData(values.linking[entity]);
    }
  }, [entity, values.linking])

  const ajaxModalValues = useAjaxModal({
    name: k,
    namespace: form?.meta?.namespace,
    isAjaxModal,
    formScope,
  })

  const data = useMemo(() => {
    const _formData = isSingle ? formData && !Array.isArray(formData) ? [formData] : [] : formData

    return _formData.map((f, i) => {
      if (isAjaxModal && typeof f === 'string' && linkingData[f]) {
        const value = linkingData[f];
        return { ...value, key: i };
      }

      if (form?.meta?.ajaxOptionsKey) {
        if (linkingData[f[form?.meta?.ajaxOptionsKey]]) {
          const value = linkingData[f[form?.meta?.ajaxOptionsKey]];
          const resData = { ...f, ...value, key: i };
          if (resData.id && !linkingData[resData.id]) {
            setLinkingData({
              ...linkingData,
              [resData.id]: resData,
            })
          }
          return resData;
        }

        const options = ajaxOptions[form?.meta?.ajaxOptionsKey];

        if (Array.isArray(options)) {
          const value = options.find((v) => v.value === f[form?.meta?.ajaxOptionsKey])
          if (value) {
            const resData = { ...f, ...value, key: i };
            if (resData.id && !linkingData[resData.id]) {
              setLinkingData({
                ...linkingData,
                [resData.id]: resData,
              })
            }
            return resData;
          }
        }
      }

      const resData = { ...f, key: i };
      if (resData.id && !linkingData[resData.id]) {
        setLinkingData({
          ...linkingData,
          [resData.id]: resData,
        })
      }
      return resData;
    })
  }, [isAjaxModal, formData, form, ajaxOptions, linkingData, isSingle]);

  const dataSource = useMemo(() => {
    if (notApplicable || notAvailable) {
      return [];
    }

    if (data.length > 3) {
      return data;
    }

    const max = isSingle ? 1 : 3;

    const emptyDataSource = [];

    for (let i = 0; i < max; i++) {
      emptyDataSource.push({ key: i, isEmpty: true });
    }

    return emptyDataSource.map((empty, i) => data[i] || empty);
  }, [data, isSingle, notApplicable, notAvailable]);

  const inputs = useMemo(() =>
    Object.values(ajaxModalValues.form)[0]
      ? Object.values(ajaxModalValues.form)[0]
      : form?.inputs || {}
    , [form, ajaxModalValues.form])

  const mapKey = useCallback((key, i) => {
    const title = (inputs[key] || {}).tableLabel || (inputs[key] || {}).label;

    return {
      title: <Tooltip title={title}>{title}</Tooltip>,
      key: key,
      dataIndex: key,
      ellipsis: { showTitle: true },
      render: (v, all) => {
        if (all.isEmpty || ajaxModalValues.isFetching) {
          return <div className="daf-default-cell" />;
        }

        const render = (title) => {
          if (tooltipInputs.includes(type)) {
            return (
              <Tooltip title={title}>
                {title} {' '}
              </Tooltip>
            )
          }

          return title;
        };

        const type = inputs[key]?.type;

        if (v || type === 'ajaxSelect') {
          if (type === 'ajaxSelect') {
            const data = all[key];

            if (data === 'new') {
              return render(
                <BasicInput
                  name={key}
                  key={key}
                  config={{
                    ...inputs[key],
                    type: 'text',
                  }}
                  valueOnlyString
                  data={{ ...all, [key]: (all?.meta?.inputs || {})[key]?.name }}
                />
              );
            }

            const { meta } = inputs[key];

            if (meta.onNewSetValueKey) {
              return render(
                <BasicInput
                  name={key}
                  key={key}
                  config={{
                    ...inputs[key],
                    type: 'text',
                  }}
                  valueOnlyString
                  data={{ ...all, [key]: all[meta.onNewSetValueKey] }}
                />
              );
            }
          }

          return render(
            <BasicInput
              name={key}
              key={key}
              config={inputs[key]}
              valueOnlyString
              data={all}
            />
          );
        }

        if ((inputs[key] || {}).noValue) {
          // console.log(i18next.language)
          return render(inputs[key] || {}).noValue[i18next.language]
        }
      },
    }
  }, [inputs, ajaxModalValues]);

  const columns = useMemo(() => [
    ...(Array.isArray(tableKeys) ? tableKeys.map(mapKey) : Object.keys(inputs || {}).map(mapKey)),
    {
      key: 'actions',
      dataIndex: 'actions',
      label: '',
      width: 50,
      render: (_, all) => {
        if (all.isEmpty || ajaxModalValues.isFetching) {
          return <div className="daf-default-cell" />;
        }

        return (
          <div
            onClick={() => setModalRow(all)}
            className="cursor-pointer flex justify-content-center">
            <EyeOutlined />
          </div>
        )
      }
    }
  ], [mapKey, inputs, tableKeys, ajaxModalValues]);

  return (
    <div className={formatClassname(['data-link-cont mt-2', noBody && 'no-body'])} style={form?.meta?.style}>
      <div className="daf-table-wrapper no-padding repeatable-form-table no-pagination">
        <ConfigProvider renderEmpty={() => notApplicable ? t('Not applicable') : t('No information available')}>
          <Table
            locale={getLocales(t)}
            loading={ajaxModalValues.isFetching}
            pagination={false}
            columns={columns}
            dataSource={dataSource}
            rowKey="key"
            scroll={noBody ? undefined : { y: 170 }}
          />
        </ConfigProvider>
      </div>

      <Modal
        open={!!modalRow}
        title={(<ModalHeader title={form?.modalLabel || form.label} />)}
        footer={null}
        width={650}
        className="max-h-50"
        onCancel={() => setModalRow(null)}
      >
        <Style>
          <div className="view-content">
            <div className="content no-padding">
              <div className="wrapper">
                <div className='group'>
                  <Input
                    config={
                      Object.keys(inputs || {})
                        .filter((k) => {
                          return !(form?.meta?.excludedKeysView || []).includes(k)
                            && !['id', 'label', 'position', 'subTitle'].includes(k)
                        })
                        .reduce((all, key) => {
                          all[key] = inputs[key];
                          return all;
                        }, {})
                    }
                    data={{ ...modalRow }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Style>
      </Modal>
    </div>
  )
}
